import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";

type WalletProvider = "fire" | "paynetics" | "isx";

type PayneticsTransactionType = "wire-transfer" | "load" | "internal-transfer";

type SimulatorBankAccount = {
  id: string;
  name?: string;
  accountNumber?: string;
  sortCode?: string;
  externalBankAccountId?: string;
  walletConnectionId: string;
};

type SimulatorConnection = {
  id: string;
  provider: string;
  status: string;
  bankAccounts: SimulatorBankAccount[];
};

type SimulatorContext = {
  connections: SimulatorConnection[];
};

type Props = {
  companyId: string;
};

function parseGbpAmount(input: string): number | null {
  const trimmed = input.trim().replace(/^£\s*/, "");
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  if (!Number.isInteger(minor) || minor <= 0) return null;
  if (Math.abs(value * 100 - minor) > 1e-9) return null;
  return minor / 100;
}

const IncomingWebhookSimulator: React.FC<Props> = ({ companyId }) => {
  const { showNotification } = useNotification();
  const [provider, setProvider] = useState<WalletProvider>("fire");
  const [context, setContext] = useState<SimulatorContext | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [walletConnectionId, setWalletConnectionId] = useState("");
  const [bankAccountId, setBankAccountId] = useState("");
  const [reference, setReference] = useState("");
  const [amountBeforeChargesGbp, setAmountBeforeChargesGbp] = useState("10.00");
  const [amountAfterChargesGbp, setAmountAfterChargesGbp] = useState("10.00");
  const [amountGbp, setAmountGbp] = useState("10.00");
  const [transactionType, setTransactionType] =
    useState<PayneticsTransactionType>("wire-transfer");
  const [lastFireTxnId, setLastFireTxnId] = useState<number | null>(null);
  const [lastPayneticsToken, setLastPayneticsToken] = useState<string | null>(
    null
  );
  const [lastIsxUid, setLastIsxUid] = useState<string | null>(null);

  const contextPath =
    provider === "fire"
      ? "/wallets/test/fire/context"
      : provider === "paynetics"
      ? "/wallets/test/paynetics/context"
      : "/wallets/test/isx/context";

  const loadContext = useCallback(async () => {
    if (!companyId) return;
    setLoadingContext(true);
    try {
      const data = (await api.get(
        `${contextPath}?companyId=${encodeURIComponent(companyId)}`
      )) as SimulatorContext;
      setContext(data);
      const firstConn = data.connections[0];
      if (firstConn) {
        setWalletConnectionId(firstConn.id);
        const firstBa = firstConn.bankAccounts[0];
        setBankAccountId(firstBa?.id ?? "");
      } else {
        setWalletConnectionId("");
        setBankAccountId("");
      }
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(
          error,
          `Failed to load ${provider} simulator context`
        ),
        NotificationTypes.WARNING
      );
    } finally {
      setLoadingContext(false);
    }
  }, [companyId, contextPath, provider, showNotification]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const selectedConnection = useMemo(
    () => context?.connections.find((c) => c.id === walletConnectionId),
    [context, walletConnectionId]
  );

  const bankAccounts = selectedConnection?.bankAccounts ?? [];

  const selectedBankAccount = useMemo(
    () => bankAccounts.find((ba) => ba.id === bankAccountId),
    [bankAccounts, bankAccountId]
  );

  useEffect(() => {
    if (!bankAccounts.some((ba) => ba.id === bankAccountId)) {
      setBankAccountId(bankAccounts[0]?.id ?? "");
    }
  }, [bankAccounts, bankAccountId]);

  const hasConnections =
    (context?.connections.length ?? 0) > 0 &&
    context!.connections.some((c) => c.bankAccounts.length > 0);

  const payneticsMissingAccountToken =
    provider === "paynetics" &&
    !!selectedBankAccount &&
    !selectedBankAccount.externalBankAccountId?.trim();
  const isxMissingAccountNumber =
    provider === "isx" &&
    !!selectedBankAccount &&
    !selectedBankAccount.accountNumber?.trim();

  const onSubmit = async () => {
    if (!walletConnectionId || !bankAccountId) {
      showNotification(
        "Select a wallet connection and bank account",
        NotificationTypes.WARNING
      );
      return;
    }
    if (!reference.trim()) {
      showNotification(
        "Payment reference is required",
        NotificationTypes.WARNING
      );
      return;
    }

    if (provider === "fire") {
      const amountBefore = parseGbpAmount(amountBeforeChargesGbp);
      const amountAfter = parseGbpAmount(amountAfterChargesGbp);
      if (amountBefore == null) {
        showNotification(
          "Amount before charges must be a positive GBP value (e.g. 25.00)",
          NotificationTypes.WARNING
        );
        return;
      }
      if (amountAfter == null) {
        showNotification(
          "Amount after charges must be a positive GBP value (e.g. 25.00)",
          NotificationTypes.WARNING
        );
        return;
      }

      setSubmitting(true);
      setLastFireTxnId(null);
      try {
        const result = (await api.post(
          "/wallets/test/fire/incoming-transaction",
          {
            companyId,
            walletConnectionId,
            bankAccountId,
            reference: reference.trim(),
            amountBeforeChargesGbp: amountBefore,
            amountAfterChargesGbp: amountAfter,
          }
        )) as { ok: boolean; txnId: number; toolkitStatus: number };
        setLastFireTxnId(result.txnId);
        showNotification(
          `Webhook sent (txnId ${result.txnId}, HTTP ${result.toolkitStatus})`,
          NotificationTypes.SUCCESS
        );
      } catch (error: unknown) {
        showNotification(
          getApiErrorMessage(error, "Failed to simulate incoming transaction"),
          NotificationTypes.WARNING
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (provider === "isx") {
      if (isxMissingAccountNumber) {
        showNotification(
          "Selected bank account is missing an account number",
          NotificationTypes.WARNING
        );
        return;
      }

      const amount = parseGbpAmount(amountGbp);
      if (amount == null) {
        showNotification(
          "Amount must be a positive GBP value (e.g. 25.00)",
          NotificationTypes.WARNING
        );
        return;
      }

      setSubmitting(true);
      setLastIsxUid(null);
      try {
        const result = (await api.post(
          "/wallets/test/isx/incoming-transaction",
          {
            companyId,
            walletConnectionId,
            bankAccountId,
            reference: reference.trim(),
            amountGbp: amount,
          }
        )) as { ok: boolean; uid: string; toolkitStatus: number };
        setLastIsxUid(result.uid);
        showNotification(
          `Webhook sent (uid ${result.uid}, HTTP ${result.toolkitStatus})`,
          NotificationTypes.SUCCESS
        );
      } catch (error: unknown) {
        showNotification(
          getApiErrorMessage(error, "Failed to simulate incoming transaction"),
          NotificationTypes.WARNING
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (payneticsMissingAccountToken) {
      showNotification(
        "Selected bank account is missing externalBankAccountId (Paynetics account token)",
        NotificationTypes.WARNING
      );
      return;
    }

    const amount = parseGbpAmount(amountGbp);
    if (amount == null) {
      showNotification(
        "Amount must be a positive GBP value (e.g. 25.00)",
        NotificationTypes.WARNING
      );
      return;
    }

    setSubmitting(true);
    setLastPayneticsToken(null);
    try {
      const result = (await api.post(
        "/wallets/test/paynetics/incoming-transaction",
        {
          companyId,
          walletConnectionId,
          bankAccountId,
          reference: reference.trim(),
          amountGbp: amount,
          transactionType,
        }
      )) as { ok: boolean; token: string; toolkitStatus: number };
      setLastPayneticsToken(result.token);
      showNotification(
        `Webhook sent (token ${result.token}, HTTP ${result.toolkitStatus})`,
        NotificationTypes.SUCCESS
      );
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(error, "Failed to simulate incoming transaction"),
        NotificationTypes.WARNING
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatBankLabel = (ba: SimulatorBankAccount) => {
    const parts = [
      ba.name,
      ba.accountNumber,
      ba.sortCode,
      ba.externalBankAccountId ? `ext: ${ba.externalBankAccountId}` : null,
    ].filter(Boolean);
    return parts.length ? parts.join(" · ") : ba.id;
  };

  const providerLabel =
    provider === "fire"
      ? "Fire"
      : provider === "paynetics"
      ? "Paynetics"
      : "ISX";

  return (
    <Container>
      <WarningBanner>
        DEV/QA ONLY - Posts a signed incoming-transaction webhook to
        toolkit-backend. This can trigger OB wallet reconciliation for pending
        transactions with a matching reference
        {provider === "fire" ? " and original amount before charges" : ""}.
      </WarningBanner>

      <Field>
        <Label>Provider</Label>
        <Select
          value={provider}
          onChange={(e) => setProvider(e.target.value as WalletProvider)}
        >
          <option value="fire">Fire</option>
          <option value="paynetics">Paynetics</option>
          <option value="isx">ISX</option>
        </Select>
      </Field>

      {provider === "paynetics" && (
        <InfoBanner>
          Paynetics incoming webhooks expose a single <code>amount</code> field.
          Unlike Fire, there is no separate amount-before-charges /
          amount-after-charges in the webhook payload — both are mapped to the
          same value internally.
        </InfoBanner>
      )}

      {provider === "isx" && (
        <InfoBanner>
          ISX SIIN matching uses the linked bank account number (
          <code>beneficiary_acno</code> / <code>provider_reference_code</code>).
          Sort code is not sent. Amount is posted in minor units.
        </InfoBanner>
      )}

      {loadingContext && (
        <Typography variant={TypographyVariant.BODY2}>
          Loading {providerLabel} connections…
        </Typography>
      )}

      {!loadingContext && context && !hasConnections && (
        <HelperText>
          No active {providerLabel} wallet connections with beneficiary bank
          accounts were found for this company.
        </HelperText>
      )}

      {hasConnections && (
        <Card>
          <CardTitle>Simulate incoming credit</CardTitle>
          <Field>
            <Label>Wallet connection</Label>
            <Select
              value={walletConnectionId}
              onChange={(e) => setWalletConnectionId(e.target.value)}
            >
              {context!.connections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.id} ({c.status})
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label>Beneficiary bank account</Label>
            <Select
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              disabled={bankAccounts.length === 0}
            >
              {bankAccounts.map((ba) => (
                <option key={ba.id} value={ba.id}>
                  {formatBankLabel(ba)}
                </option>
              ))}
            </Select>
            {payneticsMissingAccountToken && (
              <HintWarning>
                This bank account has no externalBankAccountId. Paynetics
                webhooks require it as the account token.
              </HintWarning>
            )}
            {isxMissingAccountNumber && (
              <HintWarning>
                This bank account has no account number. ISX SIIN matching
                requires it.
              </HintWarning>
            )}
          </Field>

          <Field>
            <Label>Payment reference</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Must match pending OB payment reference"
            />
          </Field>

          {provider === "fire" ? (
            <>
              <Field>
                <Label>Amount before charges (GBP)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={amountBeforeChargesGbp}
                  onChange={(e) => setAmountBeforeChargesGbp(e.target.value)}
                  placeholder="25.00"
                />
                <Hint>Gross amount before Fire fees, e.g. 25.00</Hint>
              </Field>

              <Field>
                <Label>Amount after charges (GBP)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={amountAfterChargesGbp}
                  onChange={(e) => setAmountAfterChargesGbp(e.target.value)}
                  placeholder="24.77"
                />
                <Hint>Net amount after Fire fees, e.g. 24.77</Hint>
              </Field>
            </>
          ) : (
            <>
              <Field>
                <Label>Amount (GBP)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={amountGbp}
                  onChange={(e) => setAmountGbp(e.target.value)}
                  placeholder="25.00"
                />
                <Hint>Credit amount in GBP major units, e.g. 25.00</Hint>
              </Field>

              {provider === "paynetics" && (
                <Field>
                  <Label>Transaction type</Label>
                  <Select
                    value={transactionType}
                    onChange={(e) =>
                      setTransactionType(
                        e.target.value as PayneticsTransactionType
                      )
                    }
                  >
                    <option value="wire-transfer">wire-transfer</option>
                    <option value="load">load</option>
                    <option value="internal-transfer">internal-transfer</option>
                  </Select>
                </Field>
              )}
            </>
          )}

          <Actions>
            <Button
              variant={ButtonVariant.CONTAINED}
              onClick={onSubmit}
              loading={submitting}
              disabled={
                submitting ||
                !bankAccountId ||
                payneticsMissingAccountToken ||
                isxMissingAccountNumber
              }
            >
              Send webhook
            </Button>
            <Button
              variant={ButtonVariant.OUTLINED}
              onClick={loadContext}
              loading={loadingContext}
              disabled={loadingContext}
            >
              Refresh
            </Button>
          </Actions>

          {lastFireTxnId != null && provider === "fire" && (
            <SuccessNote>
              Last simulated Fire txnId: <strong>{lastFireTxnId}</strong>
            </SuccessNote>
          )}
          {lastPayneticsToken != null && provider === "paynetics" && (
            <SuccessNote>
              Last simulated Paynetics token:{" "}
              <strong>{lastPayneticsToken}</strong>
            </SuccessNote>
          )}
          {lastIsxUid != null && provider === "isx" && (
            <SuccessNote>
              Last simulated ISX uid: <strong>{lastIsxUid}</strong>
            </SuccessNote>
          )}
        </Card>
      )}
    </Container>
  );
};

export default IncomingWebhookSimulator;

const Container = styled.div`
  padding: 20px 0;
  max-width: 640px;
  font-family: Montserrat, sans-serif;
`;

const WarningBanner = styled.div`
  background: rgba(249, 160, 0, 0.17);
  border: 1px solid #f9a000;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #13273f;
  font-weight: 500;
`;

const InfoBanner = styled.div`
  background: rgba(33, 150, 243, 0.1);
  border: 1px solid #2196f3;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #13273f;
  line-height: 1.45;

  code {
    font-family: monospace;
    font-size: 12px;
  }
`;

const HelperText = styled.p`
  font-size: 14px;
  color: #8181a5;
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 10px;
  border: 1px solid #dbe3eb;
  padding: 20px;
`;

const CardTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #13273f;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #8181a5;
  margin-bottom: 6px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dbe3eb;
  border-radius: 6px;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  background: white;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border: 1px solid #dbe3eb;
  border-radius: 6px;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
`;

const Hint = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #8181a5;
`;

const HintWarning = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #c62828;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const SuccessNote = styled.p`
  margin: 16px 0 0;
  font-size: 13px;
  color: #13273f;
`;
